import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Incidents = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API}/incidents`);
      setIncidents(response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      console.error('Error fetching incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API}/incidents/${id}`, { status: newStatus });
      fetchIncidents();
    } catch (error) {
      console.error('Error updating incident:', error);
    }
  };

  const filteredIncidents = filterStatus === 'All'
    ? incidents
    : incidents.filter(inc => inc.status === filterStatus);

  const stats = {
    total: incidents.length,
    open: incidents.filter(i => i.status === 'Open').length,
    inProgress: incidents.filter(i => i.status === 'In Progress').length,
    resolved: incidents.filter(i => i.status === 'Resolved').length,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-700 border-red-200';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Resolved': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open': return <XCircle className="w-4 h-4" />;
      case 'In Progress': return <Clock className="w-4 h-4" />;
      case 'Resolved': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-red-400 to-orange-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 font-heading" data-testid="incidents-title">
                  Incident & Complaint Management
                </h1>
                <p className="text-lg text-white/90 font-medium">Track and resolve animal-related incidents</p>
              </div>
              <button
                onClick={() => navigate('/report-incident')}
                data-testid="report-incident-button"
                className="bg-white text-red-600 px-6 py-3 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
              >
                <AlertCircle className="w-5 h-5 inline mr-2" />
                Report Incident
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-total-incidents">
            <div className="text-3xl font-extrabold text-primary mb-1">{stats.total}</div>
            <div className="text-sm font-bold text-text-secondary">Total Incidents</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-open">
            <div className="text-3xl font-extrabold text-red-600 mb-1">{stats.open}</div>
            <div className="text-sm font-bold text-text-secondary">Open</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-in-progress">
            <div className="text-3xl font-extrabold text-yellow-600 mb-1">{stats.inProgress}</div>
            <div className="text-sm font-bold text-text-secondary">In Progress</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-resolved">
            <div className="text-3xl font-extrabold text-green-600 mb-1">{stats.resolved}</div>
            <div className="text-sm font-bold text-text-secondary">Resolved</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-orange-100 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-text-primary font-heading">Filter by Status</h2>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-status"
              className="h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
            >
              <option value="All">All Incidents</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-indicator">
            <div className="text-xl text-text-secondary font-medium">Loading incidents...</div>
          </div>
        ) : filteredIncidents.length === 0 ? (
          <div className="text-center py-12" data-testid="no-incidents">
            <AlertCircle className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <div className="text-xl text-text-secondary font-medium mb-2">No incidents found</div>
            <p className="text-text-muted">Try adjusting your filter or report a new incident</p>
          </div>
        ) : (
          <div className="space-y-4 pb-8">
            {filteredIncidents.map((incident) => (
              <motion.div
                key={incident.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                data-testid={`incident-card-${incident.id}`}
                className="bg-white rounded-2xl shadow-sm border border-orange-100 overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${getStatusColor(incident.status)}`} data-testid="incident-status">
                          {getStatusIcon(incident.status)}
                          {incident.status}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700" data-testid="incident-type">
                          {incident.issueType}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-2 font-heading" data-testid="incident-resident">
                        {incident.residentName} - Flat {incident.flatNo}
                      </h3>
                      <p className="text-text-secondary mb-3" data-testid="incident-description">{incident.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-text-muted">
                        <span data-testid="incident-datetime">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {new Date(incident.dateTime).toLocaleString()}
                        </span>
                        {incident.animalId && (
                          <span data-testid="incident-animal-id">
                            <Eye className="w-4 h-4 inline mr-1" />
                            Animal ID: {incident.animalId.substring(0, 8)}
                          </span>
                        )}
                      </div>
                    </div>
                    {incident.photoUrl && (
                      <img
                        src={incident.photoUrl}
                        alt="Incident"
                        className="w-full md:w-48 h-32 object-cover rounded-xl"
                        data-testid="incident-photo"
                      />
                    )}
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-orange-100">
                    {incident.status === 'Open' && (
                      <button
                        onClick={() => updateStatus(incident.id, 'In Progress')}
                        data-testid="status-start-button"
                        className="flex-1 px-6 py-3 rounded-xl font-bold transition-all bg-yellow-500 text-white hover:bg-yellow-600 shadow-md hover:shadow-lg"
                      >
                        Start Working →
                      </button>
                    )}
                    {incident.status === 'In Progress' && (
                      <button
                        onClick={() => updateStatus(incident.id, 'Resolved')}
                        data-testid="status-resolve-button"
                        className="flex-1 px-6 py-3 rounded-xl font-bold transition-all bg-green-500 text-white hover:bg-green-600 shadow-md hover:shadow-lg"
                      >
                        ✓ Mark Resolved
                      </button>
                    )}
                    {incident.status === 'Resolved' && (
                      <div className="flex-1 px-6 py-3 rounded-xl font-bold bg-green-100 text-green-700 text-center">
                        ✓ Resolved
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Incidents;